import Uppy from "@uppy/core";
import Dashboard from "@uppy/dashboard";
import XHR from '@uppy/xhr-upload';
import {waitUntil} from "../../waitUntil";
import {getSingleInputByNameV3} from "../../forms/formInputsHelpers";
import {loading, stopLoading} from "../../loading";
import {randomAlphaString} from "../../inputs/random/strings";
import Axios from "axios";
import Swal from "sweetalert2";


// noinspection JSUnusedGlobalSymbols -- imported via d.ts
class UppyDashboardWrapper {
	/** @var {Uppy} */
	uppyCoreInstance
	/** @var {Dashboard} */
	uppyDashboardInstance
	/** @var {HTMLElement} */
	dashboardContainer
	/** @var {number} */
	lastDashboardContainerHeight = 0;

	settingUpExistingFiles = false;

	constructor(
		dashboardElement,
		coreOptions = {},
		dashboardOptions = {}
	) {
		if (typeof dashboardElement === 'string') {
			dashboardElement = document.querySelector(dashboardElement);
		}

		const defaultCoreOptions = {
			autoProceed: true, // Start uploads automatically
		};
		const defaultDashboardOptions = {
			inline: true,
			target: dashboardElement,

			height: 240, // Default height
			width: 10000, // Dashboard will limit itself to its container

			hideUploadButton: true,
			showRemoveButtonAfterComplete: true,
			proudlyDisplayPoweredByUppy: false,
			disableStatusBar: true,
			showLinkToFileUploadResult: true,
			theme: 'auto',
		}

		const finalCoreOptions = {...defaultCoreOptions, ...coreOptions};
		const finalDashboardOptions = {...defaultDashboardOptions, ...dashboardOptions};

		// If auto height was requested...
		if (finalDashboardOptions.height === 'auto') {
			// Set placeholder height
			finalDashboardOptions.height = 100;

			// Wait until uppy instance has been initialized
			waitUntil(() => this.uppyDashboardInstance)
				.then(() => {
					this.handleAutoDashboardHeight();
				})

		}

		// Save dashboard container in memory
		this.dashboardContainer = dashboardElement;

		// Init uppy instance with inline dashboard
		this.uppyCoreInstance = new Uppy(finalCoreOptions);
		// noinspection JSCheckFunctionSignatures
		this.uppyCoreInstance.use(Dashboard, finalDashboardOptions);
		// noinspection JSCheckFunctionSignatures
		this.uppyCoreInstance.use(XHR, {
			endpoint: this.getUploadEndpoint(),
			fieldName: 'media_file',
			headers: {
				'X-CSRF-Token': this.getCsrfToken(),
				'accept': 'application/json',
			},
			onAfterResponse: (response, retryCount) => this.handleUploadResponse(response, retryCount),
		});

		// Save instance in memory
		this.uppyDashboardInstance = this.uppyCoreInstance.getPlugin('Dashboard');

		//// All uppy instances initialized

		// Load existing files, if any
		this.addPreExistingFiles();
	}

	getDashboardContainer() {
		return this.dashboardContainer;
	}

	getDashboardWrapperContainer() {
		return this.dashboardContainer.parentElement;
	}

	handleAutoDashboardHeight() {
		if (this.isStillActive() === false) {
			// Stop updating height, uppy is dead
			return;
		}

		// Get parent height
		const parentHeight = this.getDashboardWrapperContainer().getBoundingClientRect().height;

		// If height has changed...
		if (Math.abs(this.lastDashboardContainerHeight - parentHeight) > 0.01) {
			// ... update height in memory
			this.lastDashboardContainerHeight = parentHeight;

			// ... update dashboard height
			this.uppyDashboardInstance.setOptions({
				height: parentHeight,
			})
		}

		// Wait until next frame to check again
		requestAnimationFrame(() => {
			this.handleAutoDashboardHeight();
		})
	}


	/**
	 * Checks if uppy is still active/part of the dom/running
	 * @return {boolean}
	 */
	isStillActive() {
		// Is the uppy root element in the dom?
		const isRootElementStillInContainer = this.getDashboardContainer().querySelector('.uppy-Root')

		// We also check if the container itself is still in the DOM
		const isContainerStillInDom = document.body.contains(this.getDashboardContainer());

		return isRootElementStillInContainer && isContainerStillInDom;
	}

	/**
	 * Retrieves upload endpoint from DOM
	 * @return {string}
	 */
	getUploadEndpoint() {
		// Check if an upload route was passed as a data attribute
		const uploadRouteAttributeNames = [
			'data-upload-route',
			'data-route',
			'data-upload-url',
			'data-url',
		]

		let route = '';

		for (const attributeName of uploadRouteAttributeNames) {
			const routeFromAttribute = this.getDashboardContainer().getAttribute(attributeName);
			if (routeFromAttribute) {
				route = routeFromAttribute;
				break;
			}
		}

		// If not, check if it was passed as an input
		if (route === '') {
			const uploadRouteInputNames = [
				'upload-route',
				'upload-url',
				'upload_route',
				'upload_url',
			];

			for (const routeName of uploadRouteInputNames) {
				const routeFromInput = getSingleInputByNameV3(this.getDashboardContainer(), routeName);
				if (routeFromInput) {
					route = routeFromInput;
					break;
				}
			}
		}

		// We could set a default route here...

		return route;
	}

	/**
	 * Retrieves Laravel's CSRF token from document
	 * @return {string}
	 */
	getCsrfToken() {
		return document.querySelector('meta[name="csrf-token"]').getAttribute('content');
	}

	clearFiles() {
		this.uppyCoreInstance.clear();
	}

	/**
	 * Adds already uploaded files, if no argument is passed, files
	 * are retrieved from the DOM.
	 *
	 * @param {UppyDashboardWrapperDefs.ServerFileWrapper[]|undefined} files
	 * @param silent Do not display any loading overlay
	 */
	addPreExistingFiles(files = undefined, silent = false) {
		const existingFiles = files ? files : this.getExistingFilesFromDom();

		// No files, nothing to do
		if (existingFiles.length === 0) {
			return;
		}

		// Files found, disable auto proceed until existing files have finished setting up
		this.uppyCoreInstance.setOptions({autoProceed: false});

		// Set loading overlay
		if (silent === false) {
			loading(this.dashboardContainer)
		}

		// We will store here all thumbnail promises
		const thumbnailPromises = [];

		// Add files placeholders to uppy
		existingFiles.forEach(file => {
			const uppyFileMeta = {
				serverId: file.id,

				// Uppy doesn't like duplicate filenames
				// so we trick it by adding a fake relative
				// path to each existing file
				relativePath: randomAlphaString(4),

				...file.custom_properties,
			}

			// noinspection JSCheckFunctionSignatures
			const newFileId = this.uppyCoreInstance.addFile({
				name: file.file_name,
				type: file.mime_type,
				data: new Blob(['']), // Empty placeholder blob
				meta: uppyFileMeta,
				size: file.size, // We pass the size, but it will be ignored, auto calculated from the data blob
			});

			// Mark placeholder file as already uploaded
			this.uppyCoreInstance.setFileState(newFileId, {
				progress: {uploadComplete: true, uploadStarted: true},
				uploadURL: file.url
			})

			// Setup thumbnail retrieval promises
			thumbnailPromises.push(
				Axios.get(file.thumbnail_url, {
					responseType: 'blob'
				}).then(response => {
					if (response.data) {
						// Update file state with thumbnail contents if they were returned
						this.uppyCoreInstance.setFileState(newFileId, {
							data: response.data,
							size: file.size,
						})
					}

					return {
						response,
						file
					}
				}).catch(error => {
					// Update size manually only
					this.uppyCoreInstance.setFileState(newFileId, {
						size: file.size,
					})

					return {
						error,
						file,
					}
				})
			)
		})

		// We want to display the uploader once all thumbnails have been retrieved,
		// but if they take too long we also want the user to be able to interact with it
		// while thumbnails are retrieved.
		//
		// So we create a cleanup function that will be called both when
		// all thumbnail promises are finished or after a timeout period
		const finishExistingFilesSetup = (timedOut = false) => {
			if (timedOut) {
				console.warn('Thumbnail promises timed out, cleaning up uploader status anyway.');
			}

			// Enable auto upload again
			this.uppyCoreInstance.setOptions({autoProceed: true});
			// Remove loading overlay
			stopLoading(this.dashboardContainer)
			// Clear flag
			this.settingUpExistingFiles = false;
		}
		const thumbnailTimeoutThresholdInMs = 5000; // Wait 5 seconds before letting the user interact with the uploader

		// Once all thumbnail promises have been processed...
		Axios.all(thumbnailPromises).then(() => {
			// ... finish setup
			finishExistingFilesSetup()
		})


		// ... Or if after thumbnail threshold timeout reached ...
		setTimeout(() => {
			// ... finish setup if not done already...
			if (this.settingUpExistingFiles === true) {
				// ... finish setup, regardless of thumbnail status
				finishExistingFilesSetup(true);
			}
		}, thumbnailTimeoutThresholdInMs)
	}

	// noinspection JSUnusedLocalSymbols
	/**
	 * @param {XMLHttpRequest} xhr
	 * @param {number} retryCount
	 */
	handleUploadResponse(xhr, retryCount) {
		let response = xhr.response;

		if (typeof response === 'string') {
			// Part response as json
			// Uppy should do this already, it does it somewhere, but
			// if we set responseType in the uploader, then it rejects
			// actual json responses.
			response = JSON.parse(response);
		}

		// Check if there is an error after the upload was
		// parsed by the server
		if (response.error) {
			this.handleUploadRejection(response);
		}

	}

	/**
	 * Called when a file was uploaded correctly, but the
	 * server rejected afterward
	 * @param {{error: string, message: string, id: string}} errorResponse
	 */
	handleUploadRejection(errorResponse) {
		// We must delete the file from the dashboard but uppy does not
		// give us the id, the server returns a success response, otherwise
		// uppy displays it own error message, and we end up showing two error toasts.

		// We could throw an error() here, but then message is hidden in the toast, plus
		// it will keep retrying the upload until it reaches max retry attempts, not good enough.

		// So instead we manually remove the file but uppy does not give us the related file
		// to this response, so we must retrieve the file ourselves using the returned serverId

		// Retrieve fake id returned from server
		const serverId = errorResponse.id;

		// By the time we check for this, uppy still has not marked the files
		// as uploaded, so we can't check the response property for the server id, it does not exist yet...
		const filesInDashboard = this.uppyCoreInstance.getFiles();
		filesInDashboard.forEach(file => {
			// ... so we will wait until they are available for each file in the
			// dashboard, until we find the one that this response belongs to.
			waitUntil(() => {
				// We must get a fresh copy of the file from the uppy instance.
				// noinspection JSUnresolvedReference
				return this.uppyCoreInstance.getFile(file.id).response;
			}).then(() => {
				// Get fresh file instance with response defined
				// noinspection JSUnresolvedReference
				const freshFile = this.uppyCoreInstance.getFile(file.id);

				// Check if this is the file we must remove
				// noinspection JSUnresolvedReference
				const responseJson = freshFile.response.body;

				// Related file found
				if (responseJson.id === serverId) {
					// Remove from dashboard
					// noinspection JSUnresolvedReference
					this.uppyCoreInstance.removeFile(freshFile.id);

					// And let the user know why it failed
					// noinspection JSUnresolvedReference
					const errorMessage = `Failed to upload file '${freshFile.name}': ${errorResponse.message}`;

					this.uppyCoreInstance.info(errorMessage, 'error', 5000);
				}

			})
		})
	}

	/**
	 * @return {UppyDashboardWrapperDefs.ServerFileWrapper[]}
	 */
	getExistingFilesFromDom() {
		let existingFiles = [];

		const preExistingFileDataKeys = [
			'data-files',
			'data-existing-files'
		];
		const preExistingFileInputNames = [
			'files',
			'existing-files',
			'existing_files',
		];

		preExistingFileDataKeys.forEach(key => {
			const filesString = this.getDashboardContainer().getAttribute(key);
			if (filesString) {
				try {
					existingFiles = JSON.parse(filesString);
				} catch (error) {
					console.error(`Error parsing existing files from data attribute ${key}`, error);
				}
			}
		})

		if (existingFiles.length > 0) {
			preExistingFileInputNames.forEach(key => {
				const filesString = getSingleInputByNameV3(this.getDashboardContainer(), key);
				if (filesString) {
					try {
						existingFiles = JSON.parse(filesString);
					} catch (error) {
						console.error(`Error parsing existing files from data input ${key}`, error);
					}
				}
			})
		}

		return existingFiles;
	}

	/**
	 * Returns an array of uppy files, ready to be submitted to the server
	 * @return {UppyDashboardWrapperDefs.UppyFileSubmissionWrapper}
	 */
	getFilesForSubmission() {
		// Get uploaded files
		/** @var {UppyDashboardWrapperDefs.UppyFileObjectWrapper[]} */
		const files = this.uppyCoreInstance.getFiles().filter(file => {
			// If it was a successful upload or an already existing file
			// noinspection JSUnresolvedVariable
			return (file.response && file.response.status === 200) || (file.meta.serverId !== undefined);
		}).map(file => {
			// Get the id, either from an existing file or a fresh upload
			let id;

			// noinspection JSUnresolvedVariable
			if (file.meta && file.meta.serverId) {
				// noinspection JSUnresolvedVariable
				id = file.meta.serverId;
			} else {
				// noinspection JSUnresolvedVariable
				id = file.response.body.id;
			}

			// noinspection JSUnresolvedReference
			return {
				id,
				custom_properties: this.getCustomPropertiesFromMeta(file),
			}
		});

		return {
			files
		}
	}

	getCustomPropertiesFromMeta(uppyFileInstance) {
		const customProperties = {};

		// These properties will never be sent as custom properties.
		// Used to remove internal stuff
		const blackList = [
			'relativePath',
			'serverId',
		]

		// Filter out blacklisted properties
		Object.keys(uppyFileInstance.meta ? uppyFileInstance.meta : {}).forEach(key => {
			if (blackList.includes(key) === false) {
				customProperties[key] = uppyFileInstance.meta[key];
			}
		})

		return customProperties;
	}

	/**
	 * Checks if the Uppy dashboard is in the middle of uploading files, and if so,
	 * prompts the user to decide whether to proceed or wait for the upload to finish.
	 *
	 * @return {Promise<void>}
	 */
	checkAndPromptForOngoingUploads() {
		return new Promise((resolve, reject) => {
			// Check if there are ongoing uploads in the dashboard instance
			// by looking for file with uploading progress class.
			const uploadInProgress = this.getDashboardContainer().querySelector('.uppy-Dashboard-Item.is-inprogress');

			if (uploadInProgress) {
				// If there are ongoing uploads, prompt the user and ask him if he still wants to continue
				Swal.fire({
					title: 'Files are still uploading',
					text: 'There are files still being uploaded. Do you want to continue anyway?',
					icon: 'warning',
					showCancelButton: true,
					confirmButtonText: 'Continue',
					cancelButtonText: 'Wait',
				}).then((result) => {
					if (result.isConfirmed) {
						// User chose to continue
						resolve();
					} else {
						// User chose to wait, reject promise to cancel whatever we are doing.
						reject('User cancelled due to pending uploads')
					}
				});
			} else {
				// No ongoing uploads; resolve immediately
				resolve();
			}
		});
	}
}

export {UppyDashboardWrapper}
